// components/BatchTable.tsx
import React from 'react'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  CircularProgress
} from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'
import type { Theme } from '@mui/material/styles'
import { Batch } from '../../../interfaces/Batch'
import { formatExpirationDate } from '../../../utils/dateHelper'
import { getStyleRowFromDate } from '../../../utils/colors'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableContainer: {
      maxHeight: 400
    },
    headerCell: {
      backgroundColor: theme.palette.grey[200],
      fontWeight: 600,
      textAlign: 'center'
    },
    row: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.grey[200]
      }
    },
    evenRow: {
      backgroundColor: theme.palette.grey[50]
    },
    noData: {
      padding: theme.spacing(2),
      textAlign: 'center'
    },
    smallCell: {
      padding: theme.spacing(1),
      textAlign: 'center'
    }
  })
)

interface BatchTableProps {
  batches: Batch[]
  showBatchNumber?: boolean
  showExpirationDate?: boolean
  showQuantity?: boolean
  modalType?: 'batch' | 'expiration' | 'both'
  isLoading?: boolean
  emptyText?: string
  onBatchClick: (batch: Batch) => void
  getBackgroundStyle?: (
    expirationDate: string | undefined,
    skuProductOrigin: number
  ) => React.CSSProperties | undefined
  skuProductOriginDays?: number
}

export const BatchTable: React.FC<BatchTableProps> = ({
  batches,
  showBatchNumber = true,
  showExpirationDate = false,
  showQuantity = false,
  isLoading = false,
  emptyText = 'No hay coincidencias',
  onBatchClick,
  getBackgroundStyle,
  skuProductOriginDays = 0
}) => {
  const classes = useStyles()

  if (isLoading) {
    return (
      <div className={classes.noData}>
        <CircularProgress size={24} />
      </div>
    )
  }

  if (batches.length === 0) {
    return (
      <Typography variant='body2' className={classes.noData}>
        {emptyText}
      </Typography>
    )
  }

  const renderTableHeaders = () => {
    const headers = []

    if (showBatchNumber) {
      headers.push(
        <TableCell key='batch' className={classes.headerCell}>
          Lote
        </TableCell>
      )
    }

    if (showExpirationDate) {
      headers.push(
        <TableCell key='expiration' className={classes.headerCell}>
          Fecha de Vencimiento
        </TableCell>
      )
    }

    if (showQuantity) {
      headers.push(
        <TableCell key='quantity' className={classes.headerCell}>
          Cantidad
        </TableCell>
      )
    }

    return headers
  }

  const useCorrectStyleFunction = getBackgroundStyle || getStyleRowFromDate

  const renderTableCells = (batch: Batch) => {
    const cells = []

    if (showBatchNumber) {
      cells.push(
        <TableCell
          key='batch-value'
          className={classes.smallCell}
          style={{
            ...useCorrectStyleFunction(
              batch.expirationDate as string,
              skuProductOriginDays
            ),
            fontSize: '0.8rem'
          }}
        >
          <Typography>{batch.batchNumber}</Typography>
        </TableCell>
      )
    }

    if (showExpirationDate) {
      cells.push(
        <TableCell
          key='expiration-value'
          className={classes.smallCell}
          style={{
            ...useCorrectStyleFunction(
              batch.expirationDate as string,
              skuProductOriginDays
            ),
            fontSize: '0.8rem'
          }}
        >
          {batch.expirationDate ? (
            <Typography>
              {formatExpirationDate(batch.expirationDate, 'dd-MM-yyyy')}
            </Typography>
          ) : (
            'N/A'
          )}
        </TableCell>
      )
    }

    if (showQuantity) {
      cells.push(
        <TableCell
          key='quantity-value'
          className={classes.smallCell}
          style={{
            ...useCorrectStyleFunction(
              batch.expirationDate as string,
              skuProductOriginDays
            ),
            fontSize: '0.8rem'
          }}
        >
          {batch.quantity !== undefined ? (
            <Typography>{batch.quantity}</Typography>
          ) : (
            'N/A'
          )}
        </TableCell>
      )
    }

    return cells
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table size='small' stickyHeader>
        <TableHead>
          <TableRow>{renderTableHeaders()}</TableRow>
        </TableHead>
        <TableBody>
          {batches.map((batch, idx) => (
            <TableRow
              key={batch.batchNumber + batch.expirationDate}
              className={`${classes.row} ${
                idx % 2 === 0 ? classes.evenRow : ''
              }`}
              onClick={() => onBatchClick(batch)}
            >
              {renderTableCells(batch)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
